"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useMultiValueMemoStringify = exports.useMemoStringify = exports.useResolveValuesWithPlaceholders = exports.useResolveValueWithPlaceholders = exports.useComposedPlaceholder = exports.usePlaceholders = exports.usePlaceholder = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2021 GoodData Corporation
var react_1 = require("react");
var json_stable_stringify_1 = tslib_1.__importDefault(require("json-stable-stringify"));
var base_1 = require("./base");
var context_1 = require("./context");
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var resolve_1 = require("./resolve");
/**
 * React hook to obtain/set placeholder value.
 * See {@link IPlaceholder}.
 *
 * Note: When placeholder is not provided, setting its value will result in the error.
 *
 * @public
 */
function usePlaceholder(placeholder) {
    var _a = context_1.usePlaceholdersContext(), state = _a.state, updateState = _a.updateState;
    var resolvedPlaceholderValue = base_1.isPlaceholder(placeholder)
        ? resolve_1.resolvePlaceholderValue(placeholder, state)
        : undefined;
    var setPlaceholderValue = react_1.useCallback(function (valueOrUpdateCallback) {
        updateState(function (s) {
            ts_invariant_1.default(base_1.isPlaceholder(placeholder), "usePlaceholder: Cannot set value of the placeholder - placeholder was not provided.");
            var resoledPlaceholderValue = resolve_1.resolvePlaceholderValue(placeholder, s);
            var updatedValue = valueOrUpdateCallback instanceof Function
                ? valueOrUpdateCallback(resoledPlaceholderValue)
                : valueOrUpdateCallback;
            return resolve_1.setPlaceholder(placeholder, updatedValue, s);
        });
    }, []);
    return [resolvedPlaceholderValue, setPlaceholderValue];
}
exports.usePlaceholder = usePlaceholder;
/**
 * React hook to obtain/set multiple placeholder values at once.
 * This is useful to perform placeholders atomic change.
 * See {@link IPlaceholder}.
 * @public
 */
function usePlaceholders(placeholders) {
    var _a = context_1.usePlaceholdersContext(), state = _a.state, updateState = _a.updateState;
    var resolvedPlaceholderValues = placeholders.map(function (placeholder) {
        return resolve_1.resolvePlaceholderValue(placeholder, state);
    });
    var memoizedResolvedValues = useMultiValueMemoStringify(resolvedPlaceholderValues);
    var setPlaceholderValues = react_1.useCallback(function (valueOrUpdateCallback) {
        updateState(function (s) {
            var resolvedValues = placeholders.map(function (placeholder) {
                return resolve_1.resolvePlaceholderValue(placeholder, s);
            });
            var updatedValues = typeof valueOrUpdateCallback === "function"
                ? valueOrUpdateCallback(resolvedValues)
                : valueOrUpdateCallback;
            return placeholders.reduce(function (acc, placeholder, i) {
                return resolve_1.setPlaceholder(placeholder, updatedValues[i], acc);
            }, s);
        });
    }, []);
    return [memoizedResolvedValues, setPlaceholderValues];
}
exports.usePlaceholders = usePlaceholders;
/**
 * React hook to obtain composed placeholder value.
 * Optionally provide custom context for the composed placeholders resolution.
 * See {@link IComposedPlaceholder}.
 *
 * @public
 */
function useComposedPlaceholder(placeholder, resolutionContext) {
    var state = context_1.usePlaceholdersContext().state;
    var resolvedValue = resolve_1.resolveComposedPlaceholderValue(placeholder, state, resolutionContext);
    return useMemoStringify(resolvedValue);
}
exports.useComposedPlaceholder = useComposedPlaceholder;
/**
 * React hook that resolves any value(s) that can possibly contain also placeholder(s) to actual value(s).
 * Optionally provide custom context for the composed placeholders resolution.
 *
 * @public
 */
function useResolveValueWithPlaceholders(value, resolutionContext) {
    var state = context_1.usePlaceholdersContext().state;
    var resolvedValue = resolve_1.resolveValueWithPlaceholders(value, state, resolutionContext);
    return useMemoStringify(resolvedValue);
}
exports.useResolveValueWithPlaceholders = useResolveValueWithPlaceholders;
/**
 * React hook that resolves multiple value(s) that can possibly contain also placeholder(s) to actual value(s).
 * Optionally provide custom context for the composed placeholders resolution.
 *
 * @public
 */
function useResolveValuesWithPlaceholders(values, resolutionContext) {
    var state = context_1.usePlaceholdersContext().state;
    var resolvedValues = values === null || values === void 0 ? void 0 : values.map(function (value) {
        return resolve_1.resolveValueWithPlaceholders(value, state, resolutionContext);
    });
    return useMultiValueMemoStringify(resolvedValues);
}
exports.useResolveValuesWithPlaceholders = useResolveValuesWithPlaceholders;
/**
 * Memoize value by its stringified value, to avoid new reference on each render.
 *
 * @param value - value to memoize
 * @returns - memoized value
 * @internal
 */
function useMemoStringify(value) {
    return react_1.useMemo(function () {
        return value;
    }, [json_stable_stringify_1.default(value)]);
}
exports.useMemoStringify = useMemoStringify;
/**
 * Memoize multiple values by their stringified value, to avoid new reference on each render.
 *
 * @param values - values to memoize
 * @returns - memoized values
 * @internal
 */
function useMultiValueMemoStringify(values) {
    var _a;
    var prevValues = react_1.useRef((_a = values === null || values === void 0 ? void 0 : values.map(function (v) { return ({
        hash: json_stable_stringify_1.default(v),
        value: v,
    }); })) !== null && _a !== void 0 ? _a : []);
    return react_1.useMemo(function () {
        return values === null || values === void 0 ? void 0 : values.map(function (val, idx) {
            var hash = json_stable_stringify_1.default(val);
            if (hash === prevValues.current[idx].hash) {
                return prevValues.current[idx].value;
            }
            prevValues.current[idx] = {
                hash: hash,
                value: val,
            };
            return val;
        });
    }, [json_stable_stringify_1.default(values)]);
}
exports.useMultiValueMemoStringify = useMultiValueMemoStringify;
//# sourceMappingURL=hooks.js.map